// LEGACY: Uses SCSS with CSS modules. New code should use @metarouter-private/mr-mui components instead.
// LEGACY: Uses @ion/components. New code should use @metarouter-private/mr-mui components instead.
import React, { useState } from 'react';
import pt from 'prop-types';
import cx from 'classnames';
import { Prompt } from 'react-router-dom';

import { MrInputSelectDeprecated } from '@ion/components';
import { LoadingButton, Button, Box, Copy07Icon } from '@metarouter-private/mr-mui';

import s from './index.module.scss';
import { useQuietUpdateIntegration, useInsertPlaybookRevision } from '@ion/api';
import { useUserContext } from '@ion/user';
import { getParams } from '../get-params';
import { globalModalStore } from '@ion/global';

import DownloadUploadPlaybook from 'app/playbook-editor/revision-header/download-upload';
import validateRequiredParams from './validate-required-params';
import CommitMessageModal from './CommitMessageModal';

import { TransformDrawer } from '../transform-drawer/TransformDrawer';
import { PlayCircleIcon } from '@metarouter-private/mr-mui';
import { isAllowedToTransformEvents } from '@ion/api';
import useTrackWithUser from '@ion/api/hooks/useTrackWithUser';

const PlaybookRevisionHeader = ({
  playbookState,
  navState,
  integration, // used for revision selecting
  version,
  isAllowed,
}) => {
  const [isOpenTransformDrawer, setIsOpenTransformDrawer] = useState(false);
  const [isCommitModalOpen, setIsCommitModalOpen] = useState(false);
  const { orgId, user } = useUserContext();
  const [quietUpdateIntegration] = useQuietUpdateIntegration();

  const [trackTestingSuiteOpen] = useTrackWithUser('testing_suite_open');

  const [insertPlaybook, { loading: isSavingPlaybookRevision }] = useInsertPlaybookRevision({
    orgId,
    onCompleted: async response => {
      const {
        id,
        integrationId,
        integration: { starterkitVersion },
      } = response.insert_playbooks.returning[0];

      // When revision 1 is saved, it updates the starter kit version in integration table.
      if (starterkitVersion === '0') {
        await quietUpdateIntegration({ integrationId, starterkitVersion: version });
      }

      // For revision jump(from revision 2 to revision 5), need to set navState.setRevision
      navState.setRevision(id);
      setIsCommitModalOpen(false);
    },
  });

  const handleOpenDrawer = () => {
    setIsOpenTransformDrawer(true);
    trackTestingSuiteOpen();
  };

  const handleCloseDrawer = () => {
    setIsOpenTransformDrawer(false);
  };

  const toggleTransformDrawer = () => {
    if (isOpenTransformDrawer) {
      handleCloseDrawer();
    } else {
      handleOpenDrawer();
    }
  };

  // revision options and numbers might be able to hoist
  const revisionOptions = integration.playbooks
    ? integration.playbooks.map(p => ({ value: p.id, text: `Revision ${p.revision}` }))
    : [];
  const disabled = !playbookState.hasChanges || !isAllowed || isSavingPlaybookRevision;

  const onSaveRevision = () => {
    const params = getParams(playbookState.connectionJSON, playbookState.playbook);
    try {
      validateRequiredParams(params);
    } catch (error) {
      return globalModalStore.addNotification({
        title: 'Unable to save revision',
        message: 'Required parameters are missing. Please populate all required parameters and retry.',
        code: error,
      });
    }

    setIsCommitModalOpen(true);
  };

  const handleSaveWithCommitMessage = commitMessage => {
    const params = getParams(playbookState.connectionJSON, playbookState.playbook);
    insertPlaybook({
      integrationId: integration.id,
      playbookJson: playbookState.playbook,
      connectionJson: playbookState.connectionJSON,
      commitMessage: commitMessage,
      createdById: user?.id,
      vars: null, //Deprecated field will remove but will be available for backwards compataiblity
      pipeline_variables: {
        data: params.filter(param => param.pipelineVarId).map(param => ({ name: param.name })),
      },
      staged_revisions: null,
    });
  };

  return (
    <div className={cx(s.content)}>
      <TransformDrawer
        isOpen={isOpenTransformDrawer}
        onClose={handleCloseDrawer}
        integration={integration}
        playbookState={playbookState}
      />
      <CommitMessageModal
        open={isCommitModalOpen}
        onClose={() => setIsCommitModalOpen(false)}
        onSave={handleSaveWithCommitMessage}
        loading={isSavingPlaybookRevision}
      />
      {integration.playbooks.length ? (
        <div className={s.revisions} title="Revert to:">
          <MrInputSelectDeprecated
            testId="revisionsSelect"
            name="revisions"
            placeholder="revert to"
            value={playbookState.baseRevisionId || ''}
            onChange={event => {
              if (!event.target?.value) {
                return;
              }

              navState.setRevision(event.target.value);
            }}
            options={revisionOptions}
            alphabetized={false}
          />
        </div>
      ) : (
        <div className={s.empty} />
      )}
      <div className={s.buttons}>
        {/* Tech Debt: ND: The outline variant has a transparent background which doesn't work for
            the situation where the background is a dark color. We either need to not use this kind 
            of button in this situation, or we need to have a new button variant. */}
        <Box sx={{ backgroundColor: 'common.white', borderRadius: 1 }}>
          <Button
            onClick={toggleTransformDrawer}
            startIcon={<PlayCircleIcon />}
            variant="outlined"
            color="secondary"
            disabled={!isAllowedToTransformEvents()}
          >
            Test Playbook
          </Button>
        </Box>

        <LoadingButton
          data-test-id="saveAsNewRevisionButton"
          variant="contained"
          disabled={disabled}
          loading={isSavingPlaybookRevision}
          loadingPosition="start"
          onClick={event => {
            if (!disabled) {
              onSaveRevision();
            }
            event.preventDefault();
          }}
          startIcon={<Copy07Icon />}
        >
          Save as New Revision
        </LoadingButton>

        {playbookState.hasChanges && (
          <Prompt
            message={location => confirm(`Continue to ${location.pathname}? Changes you made may not be saved.`)}
          />
        )}
        <DownloadUploadPlaybook integration={integration} playbookState={playbookState} />
      </div>
    </div>
  );
};

PlaybookRevisionHeader.propTypes = {
  integration: pt.object,
  playbookState: pt.object.isRequired,
  navState: pt.object.isRequired,
  version: pt.string,
  isAllowed: pt.bool.isRequired,
};

export default PlaybookRevisionHeader;
